import { useState, useEffect, useMemo } from "react";
import { ThermalModalHeader, ThermalModalFooter } from "../components/common/ThermalDesignerCommonComponents";
import { useThermalDesign } from "../../../ThermalDesignContext";
import { COATING_PRESETS } from "../../../ThermalDesignContext";
import { DividerLine } from "../../../SatelliteProfileImgAssets/SatelliteProfileImgAssets";
import { isFieldValid, FIELD_VALIDATION, showInvalidFieldToasts, useModalAutoClose } from "../hooks/ThermalDesignerHooks";
import ModalDropdown from "../../../../../../CommonComponents/ModalDropdown";

const SingleInputItem = ({ label, fieldName, value, unit, isEnabled = true, handleInputChange, invalidFields, setInvalidFields, required = false }) => {
  const validationFieldName = `node_${fieldName}`;
  const isInvalid = invalidFields.has(validationFieldName);
  const [localStr, setLocalStr] = useState(value !== undefined && value !== null ? String(value) : "");

  useEffect(() => {
    setLocalStr(value !== undefined && value !== null ? String(value) : "");
  }, [value]);

  const getPlaceholder = (): string => {
    if (validationFieldName in FIELD_VALIDATION) {
      const config = FIELD_VALIDATION[validationFieldName];
      return `[${config.min}-${config.max}]`;
    }
    return "0";
  };

  const handleChange = (e) => {
    const filtered = e.target.value.replace(/[^-0-9.]/g, "");
    const dotIndex = filtered.indexOf(".");
    const clamped = dotIndex >= 0 ? filtered.slice(0, dotIndex + 6) : filtered;
    setLocalStr(clamped);
    if (invalidFields.has(validationFieldName)) {
      setInvalidFields((prev) => {
        const newSet = new Set(prev);
        newSet.delete(validationFieldName);
        return newSet;
      });
    }
  };

  const handleBlur = () => {
    handleInputChange(fieldName, localStr);
    const numVal = parseFloat(localStr);
    const valid = !isNaN(numVal) && isFieldValid(validationFieldName, numVal);
    setInvalidFields((prev) => {
      const newSet = new Set(prev);
      if (valid) {
        newSet.delete(validationFieldName);
      } else {
        newSet.add(validationFieldName);
        showInvalidFieldToasts(new Set([validationFieldName]));
      }
      return newSet;
    });
  };

  return (
    <>
      <div className="infocard-component-properties-div">
        <div className="infocard-component-properties-sub-div">
          <span className="infocard-units-location-span">
            {label}
            {required && <span className="required-asterisk"> *</span>}
          </span>
          <div className="infocard-units-location-sub-div">
            <div className={`infocard-component-togglebtn-div${isEnabled ? "" : " disabled"}`}>
              <div className="infocard-component-value-div mass-value">
                <input
                  type="text"
                  className={`infocard-location-value ${isInvalid ? "infocard-location-value--invalid" : ""} input-editable`}
                  value={localStr}
                  onChange={handleChange}
                  onFocus={(e) => e.target.select()}
                  onBlur={handleBlur}
                  disabled={!isEnabled}
                  placeholder={getPlaceholder()}
                />
              </div>
              <div className="infocard-component-units-div">
                <span className="infocard-location-y-axis">{unit}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const CoatingSettingsModal = ({ onClose }) => {
  const [isModalMinimized, setIsModalMinimized] = useState(false);
  const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());
  const { nodeSpecsOptions, nodeSpecsUnits, setNodeSpecsOptions, isSatDesignerSidebarOpen, openModal } = useThermalDesign();
  useModalAutoClose("isCoatingSettingsModalOpen", ["isConfigureCoatingModalOpen"]);

  const selectedNode = nodeSpecsOptions.find((node) => node.isSelected);
  const coatingType = openModal.activeCoatingType || "mli";
  const coating = (selectedNode?.values?.coating?.[coatingType] || {}) as any;
  const [localValues, setLocalValues] = useState({
    specific_heat: coating.specific_heat ?? 0,
    absorptivity: coating.absorptivity ?? 0,
    emissivity: coating.emissivity ?? 0,
    area_density: coating.area_density ?? 0,
    thermal_conductance: coating.thermal_conductance ?? 0,
    preset_id: coating.preset_id ?? null as string | null,
  });

  useEffect(() => {
    setLocalValues({
      specific_heat: coating.specific_heat ?? 0,
      absorptivity: coating.absorptivity ?? 0,
      emissivity: coating.emissivity ?? 0,
      area_density: coating.area_density ?? 0,
      thermal_conductance: coating.thermal_conductance ?? 0,
      preset_id: coating.preset_id ?? null,
    });
  }, [selectedNode?.id, coatingType]);

  // Filter presets to only the active coating type
  const presetOptions = useMemo(() =>
    COATING_PRESETS
      .filter((p) => p.id.toLowerCase().startsWith(coatingType))
      .map((p) => ({ label: p.label, value: p.id })),
    [coatingType]
  );

  const handlePresetSelect = (presetId: string | null) => {
    if (!presetId) return;
    const preset = COATING_PRESETS.find((p) => p.id === presetId);
    if (!preset) return;
    setLocalValues({
      specific_heat: preset.specificHeat,
      absorptivity: preset.absorptivity,
      emissivity: preset.emissivity,
      area_density: preset.areaDensity,
      thermal_conductance: preset.thermalConductance,
      preset_id: preset.id,
    });
    setInvalidFields(new Set());
  };

  const header = { title: coatingType.toUpperCase(), isModalMinimized, setIsModalMinimized, setOpenModal: onClose, modalItem: null };

  const handleCancel = () => {
    onClose();
  };

  const handleInputChange = (fieldName: string, newValue: string) => {
    const numValue = parseFloat(newValue) || 0;
    setLocalValues((prev) => ({
      ...prev,
      [fieldName]: numValue,
      preset_id: null, // Clear preset when user manually edits
    }));
    const validationFieldName = `node_${fieldName}`;
    if (invalidFields.has(validationFieldName)) {
      setInvalidFields(prev => {
        const newSet = new Set(prev);
        newSet.delete(validationFieldName);
        return newSet;
      });
    }
  };

  const validateFields = (): Set<string> => {
    const fields: Array<[string, number]> = [
      ["node_specific_heat", localValues.specific_heat],
      ["node_absorptivity", localValues.absorptivity],
      ["node_emissivity", localValues.emissivity],
    ];
    return new Set(fields.filter(([k, v]) => !isFieldValid(k, v)).map(([k]) => k));
  };

  const handleSave = () => {
    const invalid = validateFields();
    setInvalidFields(invalid);
    if (invalid.size > 0) {
      showInvalidFieldToasts(invalid);
      return;
    }
    if (selectedNode) {
      const updatedOptions = nodeSpecsOptions.map((node) => {
        if (node.id === selectedNode.id) {
          if (!node.values) return node;
          const prevCoating = node.values.coating || { none: { percentage: 0 }, mli: { percentage: 0 }, osr: { percentage: 0 } };
          return {
            ...node,
            values: {
              ...node.values,
              coating: {
                ...prevCoating,
                [coatingType]: {
                  ...prevCoating[coatingType],
                  specific_heat: localValues.specific_heat,
                  absorptivity: localValues.absorptivity,
                  emissivity: localValues.emissivity,
                  area_density: localValues.area_density,
                  thermal_conductance: localValues.thermal_conductance,
                  preset_id: localValues.preset_id,
                },
              },
            },
          };
        }
        return node;
      });
      setNodeSpecsOptions(updatedOptions);
    }
    onClose();
  };

  return (
    <div className={`nodeSpecsModal customMaterialsModal coatingSettingsModal ${!isSatDesignerSidebarOpen ? "left" : "right"}`}>
      <ThermalModalHeader header={header} />
      {!isModalMinimized && (
        <>
          <div className="nodeSpecsModal__inputs">
            {/* Preset selector */}
            <div className="infocard-component-properties-div">
              <div className="infocard-component-properties-sub-div">
                <span className="infocard-units-location-span">Coating Preset</span>
                <div className="infocard-units-location-sub-div infocard-units-location-sub-div--material">
                  <ModalDropdown
                    options={presetOptions}
                    isMulti={false}
                    handleSelectedOption={(e) => handlePresetSelect(e?.value ?? null)}
                    previousOption={localValues.preset_id}
                    placeholder="Select preset..."
                    menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                    menuPosition="fixed"
                  />
                </div>
              </div>
            </div>
            <SingleInputItem
              label="Specific Heat"
              fieldName="specific_heat"
              value={localValues.specific_heat}
              unit={nodeSpecsUnits?.node_material_properties?.specific_heat}
              handleInputChange={handleInputChange}
              invalidFields={invalidFields}
              setInvalidFields={setInvalidFields}
              required
            />
            <SingleInputItem
              label="Absorptivity"
              fieldName="absorptivity"
              value={localValues.absorptivity}
              unit={nodeSpecsUnits?.node_material_properties?.absorptivity}
              handleInputChange={handleInputChange}
              invalidFields={invalidFields}
              setInvalidFields={setInvalidFields}
              required
            />
            <SingleInputItem
              label="Emissivity"
              fieldName="emissivity"
              value={localValues.emissivity}
              unit={nodeSpecsUnits?.node_material_properties?.emissivity}
              handleInputChange={handleInputChange}
              invalidFields={invalidFields}
              setInvalidFields={setInvalidFields}
              required
            />
            <SingleInputItem
              label="Area Density"
              fieldName="area_density"
              value={localValues.area_density}
              unit="kg/m²"
              handleInputChange={handleInputChange}
              invalidFields={invalidFields}
              setInvalidFields={setInvalidFields}
            />
            <SingleInputItem
              label="Thermal Conductance"
              fieldName="thermal_conductance"
              value={localValues.thermal_conductance}
              unit="W/(m²·K)"
              handleInputChange={handleInputChange}
              invalidFields={invalidFields}
              setInvalidFields={setInvalidFields}
            />
          </div>
          <div className="nodeSpecsModal__divider__div">
            <DividerLine />
          </div>
          <ThermalModalFooter onSave={handleSave} isLastNode={true} onCancel={handleCancel} saveLabel="Save & Exit" />
        </>
      )}
    </div>
  );
};

export default CoatingSettingsModal;
